/**
 * DashboardShell — unified chrome shared by all four dashboards
 * (NECS Admin, Property Manager, Staff, Tenant/Resident).
 *
 * Layout:
 *   - Sidebar (left): role-aware menu, NECS logo top-left, collapsible on mobile
 *   - Topbar (right): search slot, notification bell, user menu (login icon)
 *   - <main>: children (the dashboard body)
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import NotificationsBell from './NotificationsBell';
import ComplianceTrophy from './ComplianceTrophy';
import UserMenu from './UserMenu';
import { UserAvatar } from './UserAvatar';
import IdentityBadge, { SidebarUserInfo } from './IdentityBadge';
import { useAuth } from '../contexts/AuthContext';
import { ServiceOutageBanner } from './ServiceOutageBanner';
import { parseBadge, DEPT_LABEL_BY_CODE } from '../utils/badgeParser';
import { useAdminScopeTerms } from '../lib/adminScopeTerms';
import { DepartmentSidebarSection } from './admin/DepartmentSidebarSection';

// Friendly sidebar labels for each department (more conversational than the
// formal registration-form names).
const DEPARTMENT_FRIENDLY_LABEL = {
  'executive / c-suite':            'Executive Office',
  'operations':                     'Operations',
  'finance & accounting':           'Finance Office',
  'human resources':                'HR Office',
  'information technology':         'IT Office',
  'legal & compliance':             'Legal Office',
  'sales & business development':   'Sales Office',
  'marketing & communications':     'Marketing Office',
  'customer service':               'Customer Service',
  'procurement & supply chain':     'Procurement Office',
  'safety & risk management':       'Safety Office',
  'training & development':         'Training Office',
};

// Derive the sidebar department / role-area label from the most reliable
// source available, in order:
//   1. parsed badge number (NECS-A-DEPT-...)
//   2. user.department  field
//   3. role_type+region for field staff (Valet · NTX)
//   4. friendly role label
function deriveDeptLabel(user) {
  if (!user) return null;
  const parsed = parseBadge(user.badge_number);
  if (parsed.ok && parsed.kind === 'admin' && parsed.dept) {
    const formal = DEPT_LABEL_BY_CODE[parsed.dept];
    return DEPARTMENT_FRIENDLY_LABEL[(formal || '').toLowerCase()] || formal || parsed.dept;
  }
  if (user.department) {
    return DEPARTMENT_FRIENDLY_LABEL[user.department.toLowerCase()] || user.department;
  }
  if (user.region) {
    // Field staff — "Field TX R07 · Valet" (Feb 2026 Dual-Service Foundation).
    // primary_service takes precedence over the legacy role_type field so
    // "cleaning" agents get the correct branding without needing an
    // HR-side role_type migration.
    const svc = (user.primary_service || user.role_type || '').toString().trim();
    const svcLabel =
      svc.toLowerCase() === 'cleaning' ? 'Cleaning'
      : svc.toLowerCase() === 'both'    ? 'Valet + Cleaning'
      : svc.toLowerCase() === 'valet'   ? 'Valet'
      : svc || null;
    const parts = ['Field', user.state || 'TX', user.region].filter(Boolean);
    return svcLabel ? `${parts.join(' ')} · ${svcLabel}` : parts.join(' ');
  }
  if (user.role_type) {
    return user.role_type;
  }
  const r = (user.role || '').toLowerCase();
  if (['admin', 'company_manager'].includes(r)) return 'Executive Office';
  if (['supervisor', 'manager', 'crew_leader', 'driver'].includes(r)) return 'Field Operations';
  if (['agent', 'site_agent', 'staff'].includes(r)) return 'Site Operations';
  return null;
}

export default function DashboardShell({
  menuItems = [],
  activeKey = null,
  onMenuClick = null,
  brandLabel = 'Dashboard',
  children,
  topbarExtras = null,
  hideUserMenu = false,
  identityProperty = null,
}) {
  const { user } = useAuth();
  const scopeTerms = useAdminScopeTerms();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const closeOnEscape = event => { if (event.key === 'Escape') setMobileOpen(false); };
    const desktop = window.matchMedia('(min-width: 1024px)');
    const onResize = () => { if (desktop.matches) setMobileOpen(false); };
    window.addEventListener('keydown', closeOnEscape);
    desktop.addEventListener('change', onResize);
    return () => { window.removeEventListener('keydown', closeOnEscape); desktop.removeEventListener('change', onResize); };
  }, []);

  // Iter-190 · Global NECS-code live search. As soon as the query
  // starts with "NECS-" we hit the public lookup and drop a one-click
  // result card beneath the input.
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [necsMatch, setNecsMatch] = useState(null);
  const [necsError, setNecsError] = useState('');
  useEffect(() => {
    const q = (searchQuery || '').trim().toUpperCase();
    if (!q.startsWith('NECS-')) {
      setNecsMatch(null);
      setNecsError('');
      return;
    }
    if (q.length < 8) return; // wait for at least NECS-XX
    setNecsError('');
    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/properties/lookup/by-code/${encodeURIComponent(q)}`, { signal: controller.signal });
        const data = await res.json();
        if (!res.ok) {
          setNecsMatch(null);
          setNecsError(data?.detail || 'Not on file.');
          return;
        }
        setNecsMatch(data);
      } catch (e) {
        if (e.name !== 'AbortError') setNecsError('Lookup failed.');
      }
    }, 350);
    return () => { controller.abort(); clearTimeout(t); };
  }, [searchQuery]);

  const handleClick = (item) => {
    setMobileOpen(false);
    if (onMenuClick) onMenuClick(item);
    if (item.to) navigate(item.to);
  };

  const deptLabel = deriveDeptLabel(user);

  return (
    <div className="min-h-screen flex bg-zinc-50" data-testid="dashboard-shell">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
          data-testid="mobile-menu-backdrop"
        />
      )}

      {/* Sidebar — Iter-172 · Phase 3: always position:fixed so it
          stays locked while the main content scrolls, regardless of
          the ancestor container's overflow rules. Desktop leaves the
          slot open; mobile still slides in/out via translate-x. */}
      <aside
        className={`fixed top-0 left-0 z-50 h-screen w-[260px] flex-shrink-0 flex flex-col transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0 visible' : '-translate-x-full invisible pointer-events-none lg:translate-x-0 lg:visible lg:pointer-events-auto'
        }`}
        style={{ backgroundColor: '#0a0a0a' }}
        data-testid="dashboard-sidebar"
        data-state={mobileOpen ? 'open' : 'closed'}
        id="dashboard-sidebar"
        aria-label="Main navigation"
      >
        {/* Logo (top-left) — case height tuned so its gold bottom border
            aligns EXACTLY with the gold bottom border of the inner
            "NECS ADMIN" profile navbar on the right side
            (topbar 65px  +  inner h-20 80px  =  145px). */}
        <div
          className="flex items-center justify-between flex-shrink-0"
          style={{ borderBottom: '1px solid #C9A962', height: '146px' }}
          data-testid="sidebar-logo-case"
        >
          <Link to="/" className="flex items-center justify-center h-full w-full" data-testid="sidebar-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_dd9d11af-d6a7-4250-be19-4525ac61484c/artifacts/yzmg6t2c_53277.jpg"
              alt="Noble Empire Cleaning Services — Services You Can Trust"
              className="h-full w-full object-cover"
              data-testid="sidebar-necs-logo"
            />
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden shrink-0 w-11 h-11 flex items-center justify-center text-zinc-400 hover:text-white"
            aria-label="Close menu"
            data-testid="sidebar-close-mobile"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Iter-178 · Phase 4 — Identity badge (role + community/facility/
            region code + name/title/email) rendered directly under the
            logo for every dashboard. */}
        <IdentityBadge user={user} property={identityProperty} />

        {/* Iter-176 · Phase 1 — Department label / gold underline
            decorative section removed from every dashboard sidebar.
            Functional menu items below remain untouched. */}

        {/* Menu items pushed down so the first item aligns with the "Welcome, …" subtitle in the main column */}
        <nav
          className="flex-1 overflow-y-auto px-3 pb-3 space-y-1"
          style={{ paddingTop: '8px' }}
          data-testid="sidebar-nav"
        >
          {menuItems.map((item) => {
            const isActive = item.key === activeKey || (item.to && location.pathname === item.to);
            const Icon = item.icon;
            // Iter-103 · Item 1 — Try Live Demo entry in the main sidebar
            // menu renders on a white background. No other sidebar items
            // are affected. Match by `item.key === 'try_live_demo'` OR
            // `to === '/live-demo'`.
            const isTryDemo = item.key === 'try_live_demo' || item.to === '/live-demo';
            return (
              <DepartmentSidebarSection key={item.key} item={item} onNavigate={onMenuClick} onChildSelect={() => setMobileOpen(false)}>
              <button
                type="button"
                onClick={() => handleClick(item)}
                onMouseEnter={(e) => {
                  if (isTryDemo) return; // stays white
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'rgba(201, 169, 98, 0.12)';
                    e.currentTarget.style.color = '#C9A962';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isTryDemo) return; // stays white
                  if (!isActive) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = '#e5e5e5';
                  }
                }}
                className="w-full min-w-0 text-left flex items-center gap-2 px-2 py-2.5 rounded-md text-sm font-bold uppercase transition-colors"
                style={{
                  color: isTryDemo ? '#0a0a0a' : (isActive ? '#0a0a0a' : '#e5e5e5'),
                  backgroundColor: isTryDemo ? '#ffffff' : (isActive ? '#C9A962' : 'transparent'),
                  fontFamily: 'Barlow Condensed, sans-serif',
                  letterSpacing: '0.12em',
                  border: isTryDemo ? '1px solid #C9A962' : undefined,
                }}
                data-testid={`sidebar-link-${item.key}`}
                data-try-demo={isTryDemo || undefined}
              >
                {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
                <span className="flex-1 truncate">{item.label}</span>
                {item.badge != null && item.badge > 0 && (
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                    style={{ backgroundColor: isActive ? '#0a0a0a' : '#C9A962', color: isActive ? '#C9A962' : '#0a0a0a' }}
                  >
                    {item.badge}
                  </span>
                )}
              </button>
              </DepartmentSidebarSection>
            );
          })}
        </nav>

        {/* Iter-176 · Phase 1 — Footer user info strip removed from
            every dashboard sidebar. Iter-180 · Phase 3 restores it in
            a lightweight, non-interactive form (name/position/email
            only) anchored to the sidebar bottom above the topbar. */}
        <SidebarUserInfo user={user} property={identityProperty} />
      </aside>

      {/* Main column — Iter-172 · Phase 3: lg:ml-[260px] compensates
          for the now-always-fixed sidebar so content isn't hidden
          under it on desktop. */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-[260px]">
        {/* Topbar — matches the website's gold menu bar (company colors theme) */}
        <header
          className="sticky top-0 z-30 flex items-center justify-between px-4 lg:px-6 py-3"
          style={{ backgroundColor: '#C9A962', borderBottom: '1px solid #2D2D2D' }}
          data-testid="dashboard-topbar"
        >
          {/* Iter-178 · Phase 1 — Top bar reduced to two elements only:
              NECS logo on the left (renders inside sidebar case in this
              shell; on mobile a menu opener stands in for the missing
              sidebar); Trophy/Profile/Bell cluster on the right.
              Search bar, brand label, and menu strip were removed. */}
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden shrink-0 w-11 h-11 flex items-center justify-center hover:opacity-80"
              style={{ color: '#2D2D2D' }}
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              aria-controls="dashboard-sidebar"
              data-testid="topbar-open-menu"
            >
              <Menu className="h-6 w-6" />
            </button>
            {/* Iter-182 · Phase 7 — Global search restored. Sits between
                the logo (rendered in the sidebar) and the icon cluster.
                On mobile the search input collapses gracefully; the
                menu opener stays visible. */}
            <div className="relative flex-1 max-w-2xl hidden sm:block">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none"
                style={{ color: '#2D2D2D' }}
                aria-hidden="true"
              />
              <input
                type="search"
                placeholder={['admin', 'company_manager'].includes(user?.role)
                  ? `Search ${scopeTerms.occupants.toLowerCase()}, properties, ${scopeTerms.staff.toLowerCase()}, or NECS-…`
                  : 'Search properties, schedules, invoices, or paste NECS-…'}
                aria-label="Global search"
                className="w-full h-10 pl-9 pr-3 rounded-lg text-sm placeholder-zinc-500 focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: '#ffffff',
                  color: '#2D2D2D',
                  border: '1px solid #2D2D2D',
                  borderRadius: '8px',
                }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const q = (e.currentTarget.value || '').trim();
                    if (q.toUpperCase().startsWith('NECS-') && necsMatch) {
                      navigate(`/company-manager-dashboard?rr=${necsMatch.property_id}`);
                      setSearchQuery('');
                      setNecsMatch(null);
                      return;
                    }
                    if (q) window.dispatchEvent(new CustomEvent('necs-topbar-search', { detail: { q } }));
                  }
                }}
                data-testid="topbar-search-input"
              />
              {/* Iter-190 · NECS Property-Code inline result. Appears when
                  the user starts typing/pasting a `NECS-` code and the
                  backend resolves it. One-click jumps to Routes & Regions
                  focused on that property. */}
              {searchFocused && necsMatch && (
                <button
                  type="button"
                  onClick={() => {
                    navigate(`/company-manager-dashboard?rr=${necsMatch.property_id}`);
                    setSearchQuery('');
                    setNecsMatch(null);
                  }}
                  className="absolute left-0 right-0 top-11 bg-white border-2 border-[#FFD60A] rounded-lg shadow-xl px-4 py-3 text-left hover:bg-[#FFFBEA] transition z-50"
                  data-testid="topbar-search-necs-result"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#7a6300]">
                    Property / Facility ID match
                  </div>
                  <div className="text-sm font-bold text-zinc-900 mt-1">
                    {necsMatch.property_name}
                    <span className="ml-2 font-mono text-[11px] text-[#7a6300]">{necsMatch.necs_code}</span>
                  </div>
                  <div className="text-xs text-zinc-600">
                    {[necsMatch.city, necsMatch.state, (necsMatch.property_type || '').toUpperCase()].filter(Boolean).join(' · ')}
                  </div>
                </button>
              )}
              {searchFocused && necsError && (
                <div
                  className="absolute left-0 right-0 top-11 bg-white border border-red-300 rounded-lg shadow-xl px-4 py-2 text-xs text-red-600 z-50"
                  data-testid="topbar-search-necs-error"
                >
                  {necsError}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 mr-1 sm:mr-2">
            {topbarExtras}
            {/* Iter-180 · Phase 1 — Icon cluster order:
                  Admin / Property Management  →  Profile → Bell   (no Trophy)
                  Resident / Tenant / Field Staff → Trophy → Profile → Bell */}
            {!hideUserMenu && !['admin','company_manager','property_manager']
                .includes((user?.role || '').toLowerCase()) && (
              <div style={{ minWidth: 44, minHeight: 44 }}
                   className="flex items-center justify-center"
                   data-testid="topbar-trophy-wrap">
                <ComplianceTrophy role={user?.role} roleType={user?.role_type} primaryService={user?.primary_service} />
              </div>
            )}
            {!hideUserMenu && (
              <div style={{ minWidth: 44, minHeight: 44 }}
                   className="flex items-center justify-center"
                   data-testid="topbar-user-menu-wrap">
                <UserMenu />
              </div>
            )}
            {!hideUserMenu && (
              <div style={{ minWidth: 44, minHeight: 44 }}
                   className="flex items-center justify-center"
                   data-testid="topbar-bell-wrap">
                <NotificationsBell />
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 min-w-0" data-testid="dashboard-main">
          {/* Iter-105 · Item 1 — Unified filter is NO LONGER rendered at
              the shell level. Each department board mounts its own
              per-board filter instance via `useBoardFilter`. */}
          {user && !user.demo_namespace && <ServiceOutageBanner />}
          {children}
        </main>
      </div>
    </div>
  );
}
